import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function DisclaimerPage() {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/pt')) i18n.changeLanguage('pt');
    else if (path.startsWith('/de')) i18n.changeLanguage('de');
    else i18n.changeLanguage('en');
  }, [location.pathname, i18n]);

  const langCode = i18n.language || 'en';

  const content: Record<string, { title: string; h1: string; body: string }> = {
    en: {
      title: 'Disclaimer | CSSBuy Spreadsheet Independent Resource',
      h1: 'Disclaimer',
      body: `CSSBuy Spreadsheet is an independent informational resource and is not affiliated with, endorsed by, or operated by CSSBuy or any of its associated entities.

This website provides general information and research resources only. We do not process orders, handle payments, or facilitate any transactions. All information is provided for educational and research purposes.

Product links, categories, and references provided on this website are for informational purposes only. We do not guarantee the accuracy, availability, or quality of any products listed or referenced.

Shipping costs, pricing, and other details mentioned are subject to change. Always verify current information directly with the relevant platform or seller before making any purchase decisions.

This website is not responsible for any losses, damages, or disputes arising from purchases made through third-party platforms or services. Users should conduct their own research and due diligence before making any purchase.

All trademarks, logos, and brand names mentioned belong to their respective owners. Use of any third-party trademarks does not imply endorsement or affiliation.

By using this website, you acknowledge that you understand and agree to this disclaimer.`,
    },
    pt: {
      title: 'Aviso Legal | CSSBuy Spreadsheet Recurso Independente',
      h1: 'Aviso Legal',
      body: `CSSBuy Spreadsheet é um recurso de informação independente e não é afiliado, endossado ou operado pelo CSSBuy ou qualquer de suas entidades associadas.

Este site fornece apenas informações gerais e recursos de pesquisa. Não processamos pedidos, lidamos com pagamentos ou facilitamos transações. Todas as informações são fornecidas para fins educacionais e de pesquisa.

Links de produtos, categorias e referências fornecidos neste site são apenas para fins informativos. Não garantimos a precisão, disponibilidade ou qualidade de produtos listados ou referenciados.

Custos de envio, preços e outros detalhes mencionados estão sujeitos a alterações. Sempre verifique as informações atuais diretamente com a plataforma ou vendedor relevante antes de tomar decisões de compra.

Este site não é responsável por quaisquer perdas, danos ou disputas decorrentes de compras feitas através de plataformas ou serviços de terceiros. Os usuários devem conduzir sua própria pesquisa e diligência antes de fazer qualquer compra.

Todas as marcas registradas, logotipos e nomes de marcas mencionados pertencem aos seus respectivos proprietários. O uso de quaisquer marcas registradas de terceiros não implica endosso ou afiliação.

Ao usar este site, você reconhece que entende e concorda com este aviso legal.`,
    },
    de: {
      title: 'Haftungsausschluss | CSSBuy Spreadsheet Unabhängige Ressource',
      h1: 'Haftungsausschluss',
      body: `CSSBuy Spreadsheet ist eine unabhängige Informationsressource und ist nicht mit CSSBuy oder einem seiner verbundenen Unternehmen verbunden, von diesen unterstützt oder von diesen betrieben.

Diese Website stellt nur allgemeine Informationen und Recherche-Ressourcen bereit. Wir bearbeiten keine Bestellungen, handhaben keine Zahlungen oder erleichtern Transaktionen. Alle Informationen werden zu Bildungs- und Recherchezwecken bereitgestellt.

Produktlinks, Kategorien und Referenzen, die auf dieser Website bereitgestellt werden, dienen nur zu Informationszwecken. Wir garantieren nicht die Genauigkeit, Verfügbarkeit oder Qualität von gelisteten oder referenzierten Produkten.

Versandkosten, Preise und andere genannte Details können sich ändern. Überprüfen Sie immer aktuelle Informationen direkt bei der relevanten Plattform oder dem Verkäufer, bevor Sie Kaufentscheidungen treffen.

Diese Website ist nicht für Verluste, Schäden oder Streitigkeiten verantwortlich, die aus Käufen über Drittanbieter-Plattformen oder -Dienste entstehen. Benutzer sollten ihre eigene Recherche und Due Diligence durchführen, bevor sie einen Kauf tätigen.

Alle genannten Marken, Logos und Markennamen gehören ihren jeweiligen Eigentümern. Die Verwendung von Drittanbieter-Marken impliziert keine Unterstützung oder Verbindung.

Durch die Nutzung dieser Website erkennen Sie an, dass Sie diesen Haftungsausschluss verstehen und akzeptieren.`,
    },
  };

  const c = content[langCode] || content.en;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <header className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6 lg:px-8 bg-background-100 border-b border-background-200">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {c.h1}
          </h1>
        </div>
      </header>

      <article className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <nav className="mb-8 text-sm text-foreground-500">
            <Link to={`/${langCode}/`} className="hover:text-primary-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">Disclaimer</span>
          </nav>

          <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
            {c.body.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}